/**
 * Product Media Gallery
 * A web component for displaying product images and videos
 * with a main slider and thumbnail navigation
 */
class ProductMediaGallery extends HTMLElement {
  constructor() {
    super();
    
    // Store elements
    this.mainSlider = this.querySelector('.product-media-gallery__main-slider');
    this.slides = Array.from(this.querySelectorAll('.product-media-gallery__slide'));
    this.thumbnailsWrapper = this.querySelector('.product-media-gallery__thumbnails-wrapper');
    this.thumbnailsSlider = this.querySelector('.product-media-gallery__thumbnails');
    this.thumbnails = Array.from(this.querySelectorAll('.product-media-gallery__thumbnail'));
    this.prevButton = this.querySelector('.product-media-gallery__control--prev');
    this.nextButton = this.querySelector('.product-media-gallery__control--next');
    
    // console.log('ProductMediaGallery: Found elements', {
    //   slides: this.slides.length,
    //   thumbnails: this.thumbnails.length,
    //   prevButton: !!this.prevButton,
    //   nextButton: !!this.nextButton
    // });
    
    // State
    this.currentIndex = 0;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.touchStartY = 0;
    this.touchEndY = 0;
    this.touchStartTime = 0;
    this.isMobile = window.innerWidth < 769;
    this.enableZoom = this.dataset.enableZoom === 'true';
    this.enableVideoAutoplay = this.dataset.enableVideoAutoplay === 'true';
    this.isDragging = false;
    
    // Bind methods
    this.handleThumbnailClick = this.handleThumbnailClick.bind(this);
    this.handlePrevClick = this.handlePrevClick.bind(this);
    this.handleNextClick = this.handleNextClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleControlTouchStart = this.handleControlTouchStart.bind(this);
    this.handleDoubleClick = this.handleDoubleClick.bind(this);
  }
  
  connectedCallback() {
    // console.log('ProductMediaGallery: Connected to DOM');
    
    // Initialize the gallery
    this.initializeGallery();
    
    // Add event listeners
    this.addEventListeners();
    
    // Set up lazy loading
    this.setupLazyLoading();
  }
  
  disconnectedCallback() {
    // Remove event listeners
    this.removeEventListeners();
    
    // Disconnect intersection observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }
  
  initializeGallery() {
    // Set initial slide
    this.goToSlide(0);
    
    // Remove hidden class and add is-active to first slide
    this.slides.forEach((slide, index) => {
      slide.classList.remove('hidden');
      if (index === 0) {
        slide.classList.add('is-active');
      }
    });
    
    // Check if we're on mobile
    this.checkMobile();
  }
  
  checkMobile() {
    this.isMobile = window.innerWidth < 769;
    // console.log('ProductMediaGallery: isMobile =', this.isMobile);
  }
  
  addEventListeners() {
    // Thumbnail clicks
    this.thumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', this.handleThumbnailClick);
    });
    
    // Navigation buttons
    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.handlePrevClick);
      // Prevent touch events from propagating from control buttons
      this.prevButton.addEventListener('touchstart', this.handleControlTouchStart);
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.handleNextClick);
      // Prevent touch events from propagating from control buttons
      this.nextButton.addEventListener('touchstart', this.handleControlTouchStart);
    }
    
    // Keyboard navigation
    this.addEventListener('keydown', this.handleKeyDown);
    
    // Touch events for swipe on main slider
    this.mainSlider.addEventListener('touchstart', this.handleTouchStart, { passive: false });
    this.mainSlider.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    this.mainSlider.addEventListener('touchend', this.handleTouchEnd);
    
    // Touch events for thumbnails slider (mobile only)
    if (this.thumbnailsSlider) {
      this.thumbnailsSlider.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.thumbnailsSlider.addEventListener('touchmove', this.handleTouchMove, { passive: true });
      this.thumbnailsSlider.addEventListener('touchend', this.handleTouchEnd);
    }
    
    // Window resize event
    window.addEventListener('resize', this.handleResize);
    
    // Prevent double-tap zoom
    this.mainSlider.addEventListener('dblclick', this.handleDoubleClick);
  }
  
  removeEventListeners() {
    this.thumbnails.forEach(thumbnail => {
      thumbnail.removeEventListener('click', this.handleThumbnailClick);
    });
    
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.handlePrevClick);
      this.prevButton.removeEventListener('touchstart', this.handleControlTouchStart);
    }
    
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.handleNextClick);
      this.nextButton.removeEventListener('touchstart', this.handleControlTouchStart);
    }
    
    this.removeEventListener('keydown', this.handleKeyDown);
    
    this.mainSlider.removeEventListener('touchstart', this.handleTouchStart);
    this.mainSlider.removeEventListener('touchmove', this.handleTouchMove);
    this.mainSlider.removeEventListener('touchend', this.handleTouchEnd);
    
    if (this.thumbnailsSlider) {
      this.thumbnailsSlider.removeEventListener('touchstart', this.handleTouchStart);
      this.thumbnailsSlider.removeEventListener('touchmove', this.handleTouchMove);
      this.thumbnailsSlider.removeEventListener('touchend', this.handleTouchEnd);
    }
    
    window.removeEventListener('resize', this.handleResize);
    
    this.mainSlider.removeEventListener('dblclick', this.handleDoubleClick);
  }
  
  handleResize() {
    this.checkMobile();
  }
  
  setupLazyLoading() {
    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(this.handleIntersection, {
        rootMargin: '100px',
        threshold: 0.1
      });
      
      // Observe all images with data-src
      const lazyImages = this.querySelectorAll('img[data-src]');
      lazyImages.forEach(image => {
        this.intersectionObserver.observe(image);
      });
    } else {
      // Fallback for browsers that don't support IntersectionObserver
      const lazyImages = this.querySelectorAll('img[data-src]');
      lazyImages.forEach(image => {
        image.src = image.dataset.src;
        image.removeAttribute('data-src');
      });
    }
  }
  
  handleIntersection(entries, observer) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const image = entry.target;
        image.src = image.dataset.src;
        image.removeAttribute('data-src');
        observer.unobserve(image);
      }
    });
  }
  
  handleThumbnailClick(event) {
    // console.log('ProductMediaGallery: Thumbnail clicked');
    const thumbnail = event.currentTarget;
    const mediaId = thumbnail.dataset.mediaId;
    
    // console.log('ProductMediaGallery: Thumbnail data', {
    //   mediaId: mediaId
    // });
    
    // Find the index of the slide with matching media ID
    const index = this.slides.findIndex(slide => slide.dataset.mediaId === mediaId);
    
    // console.log('ProductMediaGallery: Found slide index', index);
    
    if (index !== -1) {
      this.goToSlide(index);
    }
  }
  
  handleControlTouchStart(event) {
    // Stop propagation to prevent the main slider from detecting this touch
    event.stopPropagation();
  }
  
  handlePrevClick(event) {
    if (event) {
      // Prevent default behavior and stop propagation
      event.preventDefault();
      event.stopPropagation();
    }
    this.goToSlide(this.currentIndex - 1);
  }
  
  handleNextClick(event) {
    if (event) {
      // Prevent default behavior and stop propagation
      event.preventDefault();
      event.stopPropagation();
    }
    this.goToSlide(this.currentIndex + 1);
  }
  
  handleKeyDown(event) {
    if (event.key === 'ArrowLeft') {
      this.handlePrevClick();
    } else if (event.key === 'ArrowRight') {
      this.handleNextClick();
    }
  }
  
  handleTouchStart(event) {
    // Only prevent default on the main slider to avoid interfering with thumbnail scrolling
    if (event.currentTarget === this.mainSlider) {
      event.preventDefault();
    }
    
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
    this.touchStartTime = Date.now();
    this.isDragging = true;
    
    // Determine if we're touching the main slider or thumbnails
    this.touchTarget = event.currentTarget === this.mainSlider ? 'main' : 'thumbnails';
    // console.log('Touch start on', this.touchTarget);
  }
  
  handleTouchMove(event) {
    if (!this.isDragging) return;
    
    // Only prevent default on the main slider to avoid interfering with thumbnail scrolling
    if (event.currentTarget === this.mainSlider) {
      event.preventDefault();
    }
    
    this.touchEndX = event.touches[0].clientX;
    this.touchEndY = event.touches[0].clientY;
  }
  
  handleTouchEnd() {
    if (!this.isDragging) return;
    
    const touchDiff = this.touchStartX - this.touchEndX;
    const touchDiffY = Math.abs(this.touchStartY - this.touchEndY);
    const touchDuration = Date.now() - this.touchStartTime;
    
    // Calculate swipe velocity (pixels per millisecond)
    const velocity = Math.abs(touchDiff) / touchDuration;
    
    // Detect swipe with improved criteria:
    // 1. Horizontal movement must be greater than vertical (to avoid scrolling)
    // 2. Must exceed minimum threshold (70px)
    // 3. Must have minimum velocity (0.3 pixels/ms)
    // 4. Must not be too long (to distinguish from scrolling)
    if (Math.abs(touchDiff) > 70 && 
        Math.abs(touchDiff) > touchDiffY * 1.5 && 
        velocity > 0.3 && 
        touchDuration < 300) {
      
      // Only navigate slides if we're on the main slider
      if (this.touchTarget === 'main') {
        if (touchDiff > 0) {
          // Swipe left, go to next
          this.handleNextClick();
        } else {
          // Swipe right, go to previous
          this.handlePrevClick();
        }
      }
      // For thumbnails, we don't need to do anything - native scrolling will handle it
    }
    
    // Reset touch coordinates and state
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.touchStartY = 0;
    this.touchEndY = 0;
    this.touchStartTime = 0;
    this.isDragging = false;
  }
  
  handleDoubleClick(event) {
    // Prevent default behavior (zooming)
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
  
  goToSlide(index) {
    // console.log('ProductMediaGallery: Going to slide', index);
    
    // Handle wrapping
    if (index < 0) {
      index = this.slides.length - 1;
    } else if (index >= this.slides.length) {
      index = 0;
    }
    
    // Update current index
    this.currentIndex = index;
    
    // Update slides - make sure we're toggling visibility correctly
    this.slides.forEach((slide, i) => {
      if (i === index) {
        slide.classList.add('is-active');
      } else {
        slide.classList.remove('is-active');
      }
      
      // console.log(`ProductMediaGallery: Slide ${i} is ${i === index ? 'active' : 'inactive'}`);
      
      // Pause any videos in non-active slides
      if (i !== index) {
        const video = slide.querySelector('video');
        if (video) {
          video.pause();
        }
      }
    });
    
    // Update thumbnails
    this.thumbnails.forEach((thumbnail, i) => {
      if (i === index) {
        thumbnail.classList.add('is-active');
        thumbnail.setAttribute('aria-current', 'true');
      } else {
        thumbnail.classList.remove('is-active');
        thumbnail.setAttribute('aria-current', 'false');
      }
    });
    
    // Scroll thumbnail into view if needed
    if (this.thumbnails[index]) {
      // Use a more reliable scrolling method
      const thumbnailsContainer = this.thumbnailsSlider;
      const activeThumb = this.thumbnails[index];
      
      if (this.isMobile) {
        // On mobile, scroll horizontally
        const containerRect = thumbnailsContainer.getBoundingClientRect();
        const thumbRect = activeThumb.getBoundingClientRect();
        
        const scrollLeft = thumbRect.left - containerRect.left - 
                          (containerRect.width / 2) + (thumbRect.width / 2);
        
        thumbnailsContainer.scrollTo({
          left: thumbnailsContainer.scrollLeft + scrollLeft,
          behavior: 'smooth'
        });
      } else {
        // On desktop, scroll vertically
        const containerRect = thumbnailsContainer.getBoundingClientRect();
        const thumbRect = activeThumb.getBoundingClientRect();
        
        const scrollTop = thumbRect.top - containerRect.top - 
                         (containerRect.height / 2) + (thumbRect.height / 2);
        
        thumbnailsContainer.scrollTo({
          top: thumbnailsContainer.scrollTop + scrollTop,
          behavior: 'smooth'
        });
      }
    }
    
    // Handle media-specific actions
    const currentSlide = this.slides[index];
    const mediaType = currentSlide.dataset.mediaType;
    
    if (mediaType === 'video' || mediaType === 'external_video') {
      const video = currentSlide.querySelector('video');
      if (video && this.enableVideoAutoplay) {
        video.play();
      }
    }
    
    // Announce slide change for screen readers
    this.announceSlideChange(index + 1);
  }
  
  announceSlideChange(slideNumber) {
    // Create or get the live region
    let liveRegion = this.querySelector('.product-media-gallery__live-region');
    
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'product-media-gallery__live-region';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.style.position = 'absolute';
      liveRegion.style.width = '1px';
      liveRegion.style.height = '1px';
      liveRegion.style.overflow = 'hidden';
      liveRegion.style.clip = 'rect(0 0 0 0)';
      this.appendChild(liveRegion);
    }
    
    // Update the live region
    liveRegion.textContent = `Image ${slideNumber} of ${this.slides.length}`;
  }
}

// Register the custom element
// console.log('Registering ProductMediaGallery custom element');
customElements.define('product-media-gallery', ProductMediaGallery);
// console.log('ProductMediaGallery custom element registered'); 